import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Tabs, TabsList, TabsTrigger, TabsContent } from '@devkit/ui';
import { Hexagon, RefreshCw } from 'lucide-react';
import { encodeHex, decodeHex } from '@devkit/tools-encoding';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

export default function HexEncoderPage() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEncode = useCallback(() => {
    setError(null);
    const result = encodeHex(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to encode.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('hex-encoder', 'encode');
  }, [input]);

  const handleDecode = useCallback(() => {
    setError(null);
    const result = decodeHex(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Invalid hex string.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('hex-encoder', 'decode');
  }, [input]);

  const handleSwap = useCallback(() => {
    setInput(output);
    setOutput('');
    setMode(mode === 'encode' ? 'decode' : 'encode');
  }, [output, mode]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('hex-encoder');
  }, []);

  const handleProcess = () => {
    if (mode === 'encode') handleEncode();
    else handleDecode();
  };

  return (
    <ToolLayout slug="hex-encoder">
      <Card>
        <CardContent className="p-6">
          <Tabs
            value={mode}
            onValueChange={(v) => {
              setMode(v as 'encode' | 'decode');
              setError(null);
            }}
          >
            <TabsList>
              <TabsTrigger value="encode">Encode</TabsTrigger>
              <TabsTrigger value="decode">Decode</TabsTrigger>
            </TabsList>

            <TabsContent value="encode">
              <p className="text-sm text-slate-400 mb-4">
                Convert text into its hexadecimal (base-16) byte representation.
              </p>
            </TabsContent>

            <TabsContent value="decode">
              <p className="text-sm text-slate-400 mb-4">
                Convert hex digits back into text. Works with or without spaces.
              </p>
            </TabsContent>
          </Tabs>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {mode === 'encode' ? 'Text Input' : 'Hex Input'}
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? 'Enter text to encode as hex...'
                  : '48656c6c6f (hex digits, spaces optional)'
              }
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex gap-2 mb-4">
            <Button onClick={handleProcess} className="gap-2">
              <Hexagon className="w-4 h-4" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline" onClick={handleSwap} className="gap-2" disabled={!output}>
              <RefreshCw className="w-4 h-4" />
              Swap
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                {mode === 'encode' ? 'Hex Output' : 'Text Output'}
              </label>
              {output && <CopyButton text={output} onCopy={() => trackCopy('hex-encoder')} />}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Result will appear here...'}
              className={`w-full h-40 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y break-all ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
